import {
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	IconButton,
	Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import { AgGridReact } from "ag-grid-react";
import {
	type ColDef,
	type DefaultMenuItem,
	type EditableCallbackParams,
	type GetContextMenuItemsParams,
	type GridApi,
	type MenuItemDef,
	type RowClickedEvent,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormsContext } from "../../context/FormsContext";
import { LazyLoading } from "@/components/loaders/Loader/LazyLoading";
import { ErrorModal } from "@dexteel/mesf-core";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { useSearchFormAssets } from "../../hooks/useSearchFormAssets";
import type { FormAsset } from "../../models/FormAsset";
import { UsersFormAssetTable } from "./usersFormAssetTable";
import { AssetNameColumn } from "./components/columns-table/assetName-column";
import { DeleteFormAssetModal } from "./components/delete-form-asset-modal/delete-form-asset-modal";
import { NewFormAssetModal } from "./components/new-form-asset-modal/new-form-asset-modal";
import { INITIAL_VALUES_FORM_ASSET } from "../../models/FormAssetInitialValue";
import { INITIAL_VALUES_USER_FORM_ASSET } from "../../models/UserFormAssetInitialValue";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

const useStyles = makeStyles((theme) => ({
	root: {
		"& h3": {
			display: "inline-block",
			marginTop: 0,
			paddingBotton: 0,
			paddingLeft: 20,
			border: "none",
			userSelect: "none",
		},
		"& .content-wrapper": {
			border: "none",
		},
		"& .ag-icon-menu": {
			display: "none",
		},
		"& .ag-root-wrapper": {
			borderRadius: 3,
		},
		"& .ag-header-cell": {
			padding: "0px !important",
			textAlign: "center !important",
			fontSize: 10,
		},
		"& .ag-header-cell-label": {
			justifyContent: "center",
		},
		"& .ag-cell": {
			padding: "0px !important",
			textAlign: "center !important",
			fontSize: 12,
			lineBreak: "auto",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			border: "0 !important",
			wordBreak: "break-word",
		},
		"& .ag-comments": {
			display: "flex",
			justifyContent: "flex-start",
			alignItems: "center",
		},
		"& .ag-selection-checkbox": {
			marginLeft: "10px !important",
		},
	},
	relative: {
		position: "relative",
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalTitleCloseWO: {
		backgroundColor: "#28a745",
		color: "white",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	responsive: {
		[theme.breakpoints.up("md")]: {
			padding: "0 10px 0 0 !important",
			marginTop: "0 !important",
		},
	},
	responsiveTwo: {
		[theme.breakpoints.up("md")]: {
			padding: "0 0 0 10px !important",
			marginTop: "0 !important",
		},
	},
	responsiveThree: {
		[theme.breakpoints.up("md")]: {
			padding: "0 !important",
		},
	},
	responsiveFour: {
		[theme.breakpoints.up("md")]: {
			padding: "0 10px 0 0 !important",
		},
	},
	responsiveFive: {
		[theme.breakpoints.up("md")]: {
			padding: "0 0 0 10px !important",
		},
	},
}));

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	setOpenAddFormAssetAndUserModal: Function;
	openAddFormAssetAndUserModal: boolean;
};

export const FormAssetsAndUsersTable = ({
	show,
	onHide,
	setOpenAddFormAssetAndUserModal,
	openAddFormAssetAndUserModal,
}: Props) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const classes = useStyles();
	const gridStyle = useMemo(
		() => ({ height: "100%", width: "100%", padding: 10 }),
		[],
	);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("lg");
	const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
	const [shouldUpdateWhenExit, setShouldUpdateWhenExit] = useState(false);
	const [formAssetId, setFormAssetId] = useState<number | null>(null);
	const [openDeleteFormAssetModal, setOpenDeleteFormAssetModal] =
		useState(false);

	const [openNewEditUserFormAsset, setOpenNewEditUserFormAsset] =
		useState(false);
	const [openDeleteUserFormAsset, setOpenDeleteUserFormAsset] = useState(false);
	const [newUserFormAssetMode, setNewUserFormAssetMode] =
		useState<boolean>(false);

	const {
		state: {
			FormName,
			formAssetsList,
			FormAsset: { FormAssetId },
		},
		actions: {
			setFormAssetsList,
			setFormId,
			setAssetId,
			setUsersFormAssetList,
			setFormAsset,
			setUserFormAssetDataSelectedInTable,
		},
	} = useFormsContext();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>("");

	const searchFormAssets = useSearchFormAssets({ setIsLoading, setError });

	const loadingOverlayComponent = useMemo<any>(() => {
		return LazyLoading;
	}, []);

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
		};
	}, []);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const rowClicked = (event: RowClickedEvent) => {
		if (event.data) {
			setFormAssetId(event.data.FormAssetId);
		}
	};

	const handleCancel = () => {
		onHide(shouldUpdateWhenExit);
		setFormAssetId(null);
		setFormAsset(INITIAL_VALUES_FORM_ASSET);
	};

	const getFormAssetContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data: FormAsset = params.node?.data;
			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (data) {
				result.push({
					name: "Add Asset",
					action: () => {
						setFormId(data?.FormId);
						setOpenAddFormAssetAndUserModal(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}
			if (data) {
				result.push("separator", {
					name: "Remove Asset",
					action: () => {
						setFormId(data?.FormId);
						setAssetId(data?.AssetId);
						setOpenDeleteFormAssetModal(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			const addNewForm: (DefaultMenuItem | MenuItemDef)[] = [];

			if (!data) {
				addNewForm.push({
					name: "Add Asset",
					action: () => {
						setOpenAddFormAssetAndUserModal(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			return data ? result : addNewForm;
		},
		[],
	);

	useEffect(() => {
		if (show) {
			setShouldUpdateWhenExit(false);
			searchFormAssets();
			setColumnDefs([]);
			setFormAssetsList([]);
			setFormAssetId(null);

			const newColumns: ColDef[] = [];

			newColumns.push({
				field: "AssetId",
				headerName: "Asset",
				sortable: false,
				flex: 7,
				minWidth: 130,
				cellRenderer: AssetNameColumn,
			}),
				newColumns.push({
					headerName: "",
					flex: 1,
					minWidth: 65,
					maxWidth: 65,
					sortable: false,
					cellClass: "ag-columns",
					wrapText: false,
					autoHeight: false,
					cellRenderer: (params: EditableCallbackParams) => {
						return (
							<Grid2
								container
								style={{
									height: "100%",
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<Grid2
									style={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
									size={{ xs: 12, md: 12 }}
								>
									<Grid2
										container
										spacing={1}
										style={{ justifyContent: "flex-end", minWidth: 30 }}
									>
										<Grid2 size={{ xs: 12, md: 12 }}>
											<Button
												fullWidth
												style={{}}
												onClick={(e) => onActionsClick(e, params)}
											>
												<FormatListBulletedSharpIcon
													style={{ height: "auto" }}
													color="action"
												/>
											</Button>
										</Grid2>
									</Grid2>
								</Grid2>
							</Grid2>
						);
					},
				});

			setColumnDefs(newColumns);
		} else setUsersFormAssetList([]);
	}, [show]);

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else gridApi?.hideOverlay();
	}, [isLoading, gridApi]);

	return (
		<>
			<Dialog
				maxWidth={maxWidth}
				open={show}
				onClose={handleCancel}
				fullWidth={true}
				style={{ margin: "0", padding: 30, width: "100% !important" }}
				className={classes.root}
			>
				<Grid2
					container
					justifyContent="center"
					style={{ height: "85vh", paddingBottom: 0 }}
				>
					<Grid2
						style={{ padding: "10px 20px", height: "7vh" }}
						size={{ md: 12, xs: 12 }}
					>
						<Typography
							variant="h5"
							style={{ margin: 0, fontWeight: 600, textAlign: "center" }}
						>
							CONFIGURE FORM {FormName}
						</Typography>
					</Grid2>
					<DialogContent
						dividers
						style={{ height: "62vh", paddingTop: "8px", overflowY: "hidden" }}
					>
						<Grid2
							container
							justifyContent="center"
							alignItems="center"
							className={classes.root}
						>
							<Grid2 className={classes.relative} size={{ md: 12, xs: 12 }}>
								<Grid2 container justifyContent="center" alignItems="center">
									<Grid2
										style={{
											height: "59vh",
											minHeight: "100%",
											border: "4px solid #ccc",
											marginLeft: "20px !important",
											borderRadius: 10,
											position: "relative",
											paddingBottom: 60,
										}}
										size={{ md: 4, xs: 6 }}
									>
										<div style={gridStyle}>
											<AgGridReact
												rowData={formAssetsList}
												columnDefs={columnDefs}
												defaultColDef={globalDefaultColDef}
												rowHeight={38}
												headerHeight={42}
												loadingOverlayComponent={loadingOverlayComponent}
												animateRows={true}
												getContextMenuItems={(e: any) =>
													getFormAssetContextMenuItems(e)
												}
												onRowClicked={rowClicked}
												rowSelection="single"
												onGridReady={(params) => setGridApi(params.api)}
											/>
										</div>
									</Grid2>
									<IconButton
										onClick={() => setOpenAddFormAssetAndUserModal(true)}
										aria-label="add"
										color="primary"
										style={{
											padding: 0,
											position: "absolute",
											left: 20,
											bottom: 20,
										}}
									>
										<AddBoxIcon style={{ width: 35, height: 35 }} />
									</IconButton>
									<UsersFormAssetTable
										FormAssetIdToSearchUserFormAssets={formAssetId as number}
										openNewEditUserFormAsset={openNewEditUserFormAsset}
										openDeleteUserFormAsset={openDeleteUserFormAsset}
										setOpenNewEditUserFormAsset={setOpenNewEditUserFormAsset}
										setOpenDeleteUserFormAsset={setOpenDeleteUserFormAsset}
										newUserFormAssetMode={newUserFormAssetMode}
										setNewUserFormAssetMode={setNewUserFormAssetMode}
										setShouldUpdateWhenExit={setShouldUpdateWhenExit}
									/>
								</Grid2>
								<NewFormAssetModal
									show={openAddFormAssetAndUserModal}
									onHide={(shouldUpdate: boolean) => {
										if (shouldUpdate) {
											searchFormAssets();
											setShouldUpdateWhenExit(true);
											setFormAssetId(null);
										}
										setOpenAddFormAssetAndUserModal(false);
									}}
								/>
								<DeleteFormAssetModal
									show={openDeleteFormAssetModal}
									onHide={(shouldUpdate: boolean) => {
										if (shouldUpdate) {
											searchFormAssets();
											setShouldUpdateWhenExit(true);
											setFormAssetId(null);
										}
										setOpenDeleteFormAssetModal(false);
									}}
								/>
							</Grid2>
						</Grid2>
					</DialogContent>

					<Grid2 justifyContent="flex-start" size={{ md: 12, xs: 12 }}>
						<DialogActions style={{ padding: "10px 20px" }}>
							<Grid2
								container
								justifyContent="flex-start"
								spacing={2}
								style={{ margin: 0 }}
							>
								<Grid2 size={{ md: 3, xs: 4 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={handleCancel}
									>
										Close
									</Button>
								</Grid2>
								<Grid2
									container
									style={{ display: "flex", justifyContent: "flex-end" }}
									size={{ md: 9, xs: 12 }}
								>
									<Grid2 style={{ paddingRight: 10 }} size={{ md: 4, xs: 4 }}>
										<Button
											fullWidth
											variant="contained"
											color="primary"
											disabled={false}
											onClick={() => setOpenAddFormAssetAndUserModal(true)}
										>
											ADD ASSET
										</Button>
									</Grid2>
									<Grid2 style={{ paddingLeft: 10 }} size={{ md: 4, xs: 4 }}>
										<Button
											fullWidth
											variant="contained"
											color="primary"
											disabled={FormAssetId ? false : true}
											onClick={() => {
												setUserFormAssetDataSelectedInTable(
													INITIAL_VALUES_USER_FORM_ASSET,
												);
												setNewUserFormAssetMode(true);
												setOpenNewEditUserFormAsset(true);
											}}
										>
											ADD USER
										</Button>
									</Grid2>
								</Grid2>
							</Grid2>
						</DialogActions>
					</Grid2>
					<ErrorModal error={error} onHide={() => setError("")} />
				</Grid2>
			</Dialog>
		</>
	);
};
